import { format, isValid, parse, parseISO } from 'date-fns';
// eslint-disable-next-line no-restricted-imports -- pre-existing Immutable usage; migrate off when touched
import { Map } from 'immutable';

export function formatBytes(bytes, decimals) {
  if (bytes == 0) {
    return '0 Bytes';
  }
  const k = 1024;
  const dm = decimals || 2;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / k ** i).toFixed(dm)) + ' ' + sizes[i];
}

export function formatDuration(ms) {
  if (ms == null || ms < 0) return '';
  const totalMins = Math.floor(ms / 60000);
  if (totalMins < 1) return '<1m';
  if (totalMins < 60) return `${totalMins}m`;
  const hours = Math.floor(totalMins / 60);
  const minutes = totalMins % 60;
  if (hours < 24) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
}

export function formatToMAC(str) {
  if (str == null) {
    return null;
  }
  const pairs = str.match(/.{1,2}/g); // Splits the string into pairs of characters
  return pairs.join('-').toUpperCase(); // Joins the pairs with dashes
}

export function getDateFormat(user = null) {
  if (user && Map.isMap(user)) {
    user = user.toJS();
  }

  let format = user?.options?.date_format;

  // If format is still null, get the browser's locale-based date format using Intl
  if (!format) {
    const browserLocale = navigator.language ?? 'en-US'; // Get browser language
    const intlFormatter = new Intl.DateTimeFormat(browserLocale);

    // Extract date parts and map them to date-fns style
    const parts = intlFormatter.formatToParts(new Date());
    const mapIntlToDateFns = {
      year: 'yyyy',
      month: 'MM',
      day: 'dd',
    };

    // Build date-fns format string
    format = parts.map(part => mapIntlToDateFns[part.type] || part.value).join('');
  }

  return format;
}

export function getTimeFormat(user = null) {
  if (user && Map.isMap(user)) {
    user = user.toJS();
  }

  let format = user?.options?.time_format;

  // If format is still null, get the browser's locale-based time format using Intl
  if (!format) {
    const browserLocale = navigator.language ?? 'en-US'; // Get browser language
    const intlFormatter = new Intl.DateTimeFormat(browserLocale, { timeStyle: 'short' });

    // Extract time parts and map them to date-fns style
    const parts = intlFormatter.formatToParts(new Date());
    const mapIntlToDateFns = {
      hour: '', // Dynamically set this based on whether we detect 'am'/'pm'
      minute: 'mm',
      second: 'ss',
      dayPeriod: 'a', // This is for AM/PM if it exists
    };

    // Determine if AM/PM is used (12-hour format)
    const usesDayPeriod = parts.some(part => part.type === 'dayPeriod');
    mapIntlToDateFns.hour = usesDayPeriod ? 'hh' : 'HH'; // Use 'hh' for 12-hour or 'HH' for 24-hour

    // Build date-fns format string for time with correct spacing
    format = parts
      .map(part => {
        // Ensure a regular space between minute and dayPeriod if AM/PM is used
        if (part.type === 'minute' && usesDayPeriod) {
          return `${mapIntlToDateFns[part.type]} `; // Add a normal space (U+0020)
        }
        return mapIntlToDateFns[part.type] || part.value;
      })
      .join('');

    // Remove any unexpected whitespace characters (non-breaking spaces, etc.)
    format = format.replace(/\u202F/g, ' ').replace('  ', ' '); // Replace non-breaking space (U+202F) with a normal space
  }

  return format;
}

export function getDateTimeFormat(user = null) {
  const dateFormat = getDateFormat(user);
  const timeFormat = getTimeFormat(user);
  return `${dateFormat} ${timeFormat}`;
}

function parseDateTime(dateTime) {
  if (!dateTime) {
    console.error('No dateTime provided');
    return null;
  }

  let parsedDate;
  const today = new Date(); // Use today's date as a base for time-only strings

  // Check if input is already a Date object and valid
  if (dateTime instanceof Date && !isNaN(dateTime)) {
    parsedDate = dateTime;
  } else {
    // Define the possible time formats to support
    const timeFormats = [
      'hh:mm a', // 12-hour time with AM/PM (e.g., '02:30 PM')
      'hh:mm:ss a', // 12-hour time with seconds and AM/PM (e.g., '02:30:45 PM')
      'HH:mm', // 24-hour time without seconds (e.g., '14:30')
      'HH:mm:ss', // 24-hour time with seconds (e.g., '14:30:45')
    ];

    // Check if the input is a time-only string and attempt to parse with various formats
    if (/^\d{1,2}:\d{2}(:\d{2})?(\s?[aApP][mM])?$/.test(dateTime)) {
      for (const formatString of timeFormats) {
        const parsed = parse(dateTime, formatString, today);
        if (isValid(parsed)) {
          parsedDate = parsed;
          break;
        }
      }
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateTime)) {
      // Parse YYYY-MM-DD as local date (not UTC)
      parsedDate = parse(dateTime, 'yyyy-MM-dd', new Date());
    } else {
      // Attempt to parse as ISO or general date string
      parsedDate = new Date(dateTime);

      // If new Date() fails, try ISO parsing with date-fns
      if (isNaN(parsedDate.getTime())) {
        parsedDate = parseISO(dateTime);

        // Final fallback to Date.parse if both methods fail
        if (!isValid(parsedDate)) {
          const timestamp = Date.parse(dateTime);
          if (!isNaN(timestamp)) {
            parsedDate = new Date(timestamp);
          } else {
            console.error('Invalid dateTime provided:', dateTime);
            return null; // Parsing failed
          }
        }
      }
    }
  }

  return isValid(parsedDate) ? parsedDate : null;
}

export function formatDateTime(dateTime, user = null) {
  const parsedDate = parseDateTime(dateTime);
  if (!parsedDate) {
    return 'Invalid DateTime';
  }

  const dateFormat = getDateFormat(user);
  const timeFormat = getTimeFormat(user);
  const dateTimeFormat = `${dateFormat} ${timeFormat}`;

  try {
    return format(parsedDate, dateTimeFormat);
  } catch (error) {
    console.error(`Failed to format dateTime using format: ${dateTimeFormat}`, error);
    return parsedDate.toLocaleString(); // Fallback to toLocaleString if format fails
  }
}

export function formatDate(dateTime, user = null) {
  const parsedDate = parseDateTime(dateTime);
  if (!parsedDate) {
    return 'Invalid Date';
  }

  const dateFormat = getDateFormat(user);

  try {
    return format(parsedDate, dateFormat);
  } catch (error) {
    console.error(`Failed to format date using format: ${dateFormat}`, error);
    return parsedDate.toLocaleDateString(); // Fallback to toLocaleString if format fails
  }
}

export function formatTime(dateTime, user = null) {
  const parsedDate = parseDateTime(dateTime);
  if (!parsedDate) {
    return 'Invalid DateTime';
  }

  const timeFormat = getTimeFormat(user);

  try {
    return format(parsedDate, timeFormat);
  } catch (error) {
    console.error(`Failed to format time using format: ${timeFormat}`, error);
    return parsedDate.toLocaleTimeString(); // Fallback to toLocaleString if format fails
  }
}

export function formatDateForSubmission(dateTime) {
  const parsedDate = parseDateTime(dateTime);
  if (!parsedDate) {
    return null;
  }

  return format(parsedDate, 'yyyy-MM-dd'); // Format date part only
}

export function getUserMeasurementSystem(user = null) {
  if (user == null) {
    return 'metric'; // Default to metric if no user provided
  }

  if (user && Map.isMap(user)) {
    user = user.toJS();
  }

  let { measurement_system } = user?.options || {}; // Safely destructuring user options
  if (measurement_system == null) {
    measurement_system = 'metric'; // Default to metric if not set
  }

  return measurement_system;
}

/**
 * Format a number per the user's `number_format` preference (thousands
 * separator + decimal mark), via the matching Intl locale.
 *
 * @param {number} number - value to format.
 * @param {object|Map|null} [user] - current user (options.number_format);
 *   falls back to `'comma-period'` when absent.
 * @param {Intl.NumberFormatOptions} [options] - passed through to
 *   `Intl.NumberFormat`, e.g. `{ minimumFractionDigits: 2, maximumFractionDigits: 2 }`
 *   for a fixed-decimal money display.
 * @returns {string} The locale-formatted number, or `'-'` when not a number.
 */
export function formatNumber(number, user = null, options = {}) {
  if (user && Map.isMap(user)) {
    user = user.toJS();
  }

  let { number_format } = user?.options || {};
  if (number_format == null) {
    number_format = 'comma-period';
  }

  let locale;
  let grouping = {};

  switch (number_format) {
    case 'comma-period':
      locale = 'en-US'; // US-style: 1,234.56
      break;
    case 'period-comma':
      locale = 'de-DE'; // German-style: 1.234,56
      break;
    case 'no-separator-period':
      // The locale only chooses the decimal mark — "no separator" itself
      // must be stated, or Intl still groups thousands.
      locale = 'en'; // 1234.56
      grouping = { useGrouping: false };
      break;
    case 'no-separator-comma':
      locale = 'fr'; // 1234,56
      grouping = { useGrouping: false };
      break;
    default:
      locale = 'en-US'; // Fallback to US-style
  }

  if (isNaN(number)) {
    return '-';
  }

  return new Intl.NumberFormat(locale, { ...grouping, ...options }).format(number);
}

/**
 * Format a user reference object as a display name.
 *
 * The odl-app user_ref shape is { id, name, email }; the legacy shape
 * uses first_name / last_name. Returns null when the ref is absent or
 * produces an empty string.
 *
 * @param {object|null} userRef - user reference from the API
 * @returns {string|null}
 */
export function userRefName(userRef) {
  if (!userRef) return null;
  const name = `${userRef.name ?? `${userRef.first_name ?? ''} ${userRef.last_name ?? ''}`}`.trim();
  return name || null;
}
